import React from 'react';
import { motion } from 'framer-motion';
import { Download, CreditCard, FileText } from 'lucide-react';
const deliverables = [{
  icon: Download,
  title: 'Instant PDF Download',
  desc: 'Access the full guide in seconds. No app, no waiting.',
  accent: 'sage',
  iconBg: 'bg-sage/8 text-sage-dark',
  glow: 'rgba(139,152,119,0.08)',
  strip: 'linear-gradient(90deg, transparent 10%, rgba(139,152,119,0.25) 50%, transparent 90%)',
  number: '01'
}, {
  icon: CreditCard,
  title: '5 Emergency Calm Cards',
  desc: 'Pocket-sized rescue scripts for the hardest moments.',
  accent: 'terracotta',
  iconBg: 'bg-terracotta/8 text-terracotta',
  glow: 'rgba(198,114,76,0.08)',
  strip: 'linear-gradient(90deg, transparent 10%, rgba(198,114,76,0.25) 50%, transparent 90%)',
  number: '02'
}, {
  icon: FileText,
  title: '1-Page Protocol Sheet',
  desc: 'Your entire reset plan on a single, scannable sheet.',
  accent: 'brown',
  iconBg: 'bg-brown/8 text-brown',
  glow: 'rgba(61,50,41,0.06)',
  strip: 'linear-gradient(90deg, transparent 10%, rgba(61,50,41,0.15) 50%, transparent 90%)',
  number: '03'
}];
export function WhatYouGet() {
  return <section className="relative py-12 md:py-20 px-6 md:px-8 bg-cream overflow-hidden" data-id="element-819">
      {/* Ambient glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[300px] bg-terracotta/4 rounded-full blur-[100px] pointer-events-none" data-id="element-820" />

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-100px'
    }} transition={{
      duration: 0.8,
      ease: [0.25, 0.1, 0.25, 1]
    }} className="max-w-3xl mx-auto relative z-10" data-id="element-821">
        <div className="text-center mb-12" data-id="element-822">
          {/* Ornamental divider */}
          <div className="flex items-center justify-center gap-3 mb-5" data-id="element-823">
            <div className="w-8 md:w-12 h-px bg-gradient-to-r from-transparent to-terracotta/30" data-id="element-824" />
            <div className="w-1.5 h-1.5 rounded-full bg-terracotta/40" data-id="element-825" />
            <div className="w-8 md:w-12 h-px bg-gradient-to-l from-transparent to-terracotta/30" data-id="element-826" />
          </div>
          <p className="text-[11px] uppercase tracking-[0.3em] text-terracotta font-bold mb-4" data-id="element-827">
            What's Inside
          </p>
          <h2 className="font-serif text-3xl md:text-4xl text-brown mb-3" data-id="element-828">
            Everything you need. Nothing you don't.
          </h2>
          <p className="font-serif text-lg text-brown/50 italic" data-id="element-829">
            Designed to work at 3am, one-handed.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5 md:gap-6" data-id="element-830">
          {deliverables.map((item, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: i * 0.12,
          ease: [0.25, 0.1, 0.25, 1]
        }} whileHover={{
          y: -3,
          transition: {
            duration: 0.3
          }
        }} className="bg-white rounded-2xl md:rounded-3xl border border-brown/[0.06] overflow-hidden flex flex-col text-center transition-shadow duration-500" style={{
          boxShadow: `0 1px 3px rgba(61,50,41,0.04), 0 8px 24px ${item.glow}`
        }} data-id="element-831">
              {/* Top accent strip */}
              <div className="h-[3px] w-full" style={{
            background: item.strip
          }} data-id="element-832" />

              <div className="p-7 md:p-8 flex flex-col items-center gap-5 relative" data-id="element-833">
                {/* Editorial number watermark */}
                <span className="absolute top-4 right-5 font-serif text-3xl font-light text-brown/[0.06] leading-none select-none" data-id="element-834">
                  {item.number}
                </span>

                <div className={`w-12 h-12 md:w-14 md:h-14 rounded-2xl flex items-center justify-center ${item.iconBg}`} style={{
              boxShadow: `inset 0 1px 4px ${item.glow}, 0 2px 8px ${item.glow}`
            }} data-id="element-835">
                  <item.icon className="w-5 h-5 md:w-6 md:h-6" strokeWidth={1.8} data-id="element-836" />
                </div>
                <div data-id="element-837">
                  <h3 className="font-serif text-lg md:text-xl text-brown font-medium mb-2 leading-tight" data-id="element-838">
                    {item.title}
                  </h3>
                  <p className="text-sm text-brown-light/70 leading-relaxed" data-id="element-839">
                    {item.desc}
                  </p>
                </div>
              </div>
            </motion.div>)}
        </div>

        {/* Closing line */}
        <motion.p initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8,
        delay: 0.4
      }} className="text-center mt-10 font-serif text-base text-brown/40 italic" data-id="element-840">
          Simple tools. Real relief. Yours in seconds.
        </motion.p>
      </motion.div>
    </section>;
}